import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Dialog } from 'primereact/dialog'
import { classNames } from 'primereact/utils'

import { useBackgroundTaskStatus } from '../../../../../../data/batchstatus/hooks'
import { EInvoiceError } from '../../../../data'

type Props = {
  locationUrl: string
  retry: string
  isDialogVisible: boolean
  setIsDialogVisible: (visible: boolean) => void
}

export const UploadDialog: React.FC<Props> = ({
  locationUrl,
  retry,
  isDialogVisible,
  setIsDialogVisible,
}) => {
  const { t } = useTranslation('eInvoiceOverview')
  const navigate = useNavigate()

  const { data: backgroundStatus } = useBackgroundTaskStatus<EInvoiceError>(
    locationUrl,
    retry,
  )

  // State for animated progress - tracks the visual progress separately from actual progress
  const [animatedProcessedItems, setAnimatedProcessedItems] = useState(0)

  // Each step completes once animatedProcessedItems reaches its threshold
  const steps = [
    { label: t('fileUpload.upload.steps.formatValidation'), completedAt: 2 },
    {
      label: t('fileUpload.upload.steps.conformityValidation'),
      completedAt: 4,
    },
    { label: t('fileUpload.upload.steps.duplicateValidation'), completedAt: 5 },
    {
      label: t('fileUpload.upload.steps.masterDataValidation'),
      completedAt: 6,
    },
    { label: t('fileUpload.upload.steps.renderPreview'), completedAt: 10 },
  ]

  const totalItems = steps[steps.length - 1].completedAt

  const currentVisualStep = (() => {
    const idx = steps.findIndex(s => animatedProcessedItems < s.completedAt)
    return idx === -1 ? steps.length : idx
  })()

  // Handle animated progress updates
  useEffect(() => {
    if (!backgroundStatus?.processedItems) return

    const targetProcessedItems = backgroundStatus.processedItems

    // If the target is higher than our animated progress, start catching up
    if (targetProcessedItems > animatedProcessedItems) {
      const timer = setTimeout(() => {
        setAnimatedProcessedItems(prev => prev + 1)
      }, 500) // Wait 0.5 second between each step

      return () => clearTimeout(timer)
    }
  }, [backgroundStatus?.processedItems, animatedProcessedItems])

  // Process status changes and navigate accordingly
  useEffect(() => {
    // Only proceed if data is available and the dialog is visible
    if (!backgroundStatus || !isDialogVisible) return

    // Phase 1 means processing is complete
    if (backgroundStatus.phase === 1) {
      const hasError =
        backgroundStatus?.errors?.Results?.some(
          error => error.Level === 'Error',
        ) ?? false

      const hasSuccess = !!backgroundStatus.resourceUrl

      // If there are errors, navigate immediately without waiting for animation
      if (hasError) {
        // Close dialog immediately
        setIsDialogVisible(false)

        // Navigate to error page immediately
        navigate('/e-invoices/errors', {
          state: { errors: backgroundStatus.errors },
        })
        return
      }

      // For success case, wait for animation to complete
      if (
        hasSuccess &&
        backgroundStatus.resourceUrl &&
        animatedProcessedItems >= totalItems
      ) {
        // Wait at least 0.5 second after showing the final step before navigating
        const navigationTimer = setTimeout(() => {
          // Close dialog
          setIsDialogVisible(false)

          // Navigation to success page
          if (backgroundStatus.resourceUrl) {
            navigate(backgroundStatus.resourceUrl, {
              state: { isFirstUpload: true, errors: backgroundStatus.errors },
            })
          }
        }, 500) // Wait 0.5 second after final step is shown

        return () => clearTimeout(navigationTimer)
      }
    }
  }, [
    backgroundStatus,
    isDialogVisible,
    navigate,
    setIsDialogVisible,
    animatedProcessedItems,
    steps.length,
  ])

  return (
    <Dialog
      visible={isDialogVisible}
      onHide={() => null}
      closable={false}
      closeOnEscape={false}
      dismissableMask={false}
      draggable={false}
      resizable={false}
      blockScroll
      contentClassName="text-center px-8"
    >
      <i
        className="pi pi-cloud-upload text-blue-500"
        style={{ fontSize: '3rem' }}
      ></i>

      <h3 className="mb-2 mt-4">{t('fileUpload.upload.title')}</h3>
      <p className="mt-0 mb-4">{t('fileUpload.upload.description')}</p>

      <div className="flex flex-column gap-3">
        {steps.map((step, index) => {
          const isCompleted = index < currentVisualStep
          const isCurrentStep = index === currentVisualStep

          return (
            <div key={index} className="flex align-content-center">
              <div className="w-1 mr-2">
                {isCompleted && <i className="pi pi-check text-green-500"></i>}

                {isCurrentStep && (
                  <i className="pi pi-spin pi-spinner text-blue-500"></i>
                )}

                {!isCompleted && !isCurrentStep && (
                  <i className="pi pi-circle text-gray-300"></i>
                )}
              </div>
              <span
                className={classNames({
                  'text-green-600': isCompleted,
                  'text-blue-600': isCurrentStep,
                  'text-gray-300': !isCompleted && !isCurrentStep,
                })}
              >
                {step.label}
              </span>
            </div>
          )
        })}
      </div>
    </Dialog>
  )
}
